/**
 * AdminCorneliusTasks
 *
 * Inner content for /admin/cornelius-tasks.  Wrapped by AdminCorneliusTasksPage
 * (in App.js) which provides Sidebar + top-nav + content-wrapper chrome.
 *
 * Styling rules:
 *   - Uses CSS variables defined in App.css (--card-bg, --text-primary, etc.)
 *     so the page automatically tracks light/dark theme toggles.
 *   - No fixed full-screen background or font-family — those come from the
 *     surrounding chrome.
 */

import React, { useState, useEffect, useCallback } from 'react';

const STATUS_COLOR = {
  queued:    'var(--text-secondary)',
  running:   'var(--primary-color)',
  complete:  'var(--success-color)',
  failed:    'var(--danger-color)',
  cancelled: 'var(--text-secondary)',
  abandoned: 'var(--danger-color)',
  awaiting_approval: 'darkorange',
};

const cardStyle = {
  background:   'var(--card-bg)',
  border:       '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius)',
  padding:      '12px 16px',
  marginBottom: 10,
  color:        'var(--text-primary)',
  boxShadow:    'var(--shadow-sm)',
};

const inputStyle = {
  padding: '7px 12px',
  borderRadius: 'var(--border-radius)',
  border: '1px solid var(--border-color)',
  background: 'var(--surface-color)',
  color: 'var(--text-primary)',
  fontSize: 13,
  outline: 'none',
};

function btn(variant) {
  const map = {
    primary: { background: 'var(--primary-color)', color: 'var(--text-on-primary)' },
    ghost:   { background: 'transparent',          color: 'var(--text-secondary)', border: '1px solid var(--border-color)' },
  };
  return {
    padding: '7px 16px', borderRadius: 'var(--border-radius)', border: 'none',
    fontSize: 13, cursor: 'pointer', fontWeight: 600,
    ...map[variant],
  };
}

function Badge({ s }) {
  return (
    <span style={{
      fontSize: 11,
      padding: '2px 7px',
      borderRadius: 10,
      background: 'var(--surface-color)',
      border: '1px solid var(--border-color)',
      color: STATUS_COLOR[s] || 'var(--text-secondary)',
      fontWeight: 600,
    }}>{s}</span>
  );
}

function SubtaskTrace({ log }) {
  if (!log || !log.length) {
    return <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No subtask trace.</div>;
  }
  return (
    <div>
      {log.map((s, i) => (
        <div key={i} style={{
          borderLeft: `2px solid ${s.success === false ? 'var(--danger-color)' : 'var(--success-color)'}`,
          paddingLeft: 10, marginBottom: 6,
        }}>
          <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>
            <strong>#{s.id}</strong> {s.goal}
            <code style={{ marginLeft: 8, color: 'var(--primary-color)', fontSize: 11 }}>{s.tool}</code>
          </div>
          {s.result_summary && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
              {s.result_summary.slice(0, 300)}
            </div>
          )}
          {s.finished_at && (
            <div style={{ fontSize: 10, color: 'var(--primary-color)', marginTop: 2 }}>
              {new Date(s.finished_at).toLocaleString()}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function TaskRow({ task, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [resumeTok, setResumeTok] = useState('');
  const [resumeMsg, setResumeMsg] = useState('');
  const elapsed = task.finished_at && task.created_at
    ? Math.round((new Date(task.finished_at) - new Date(task.created_at)) / 1000)
    : null;

  async function sendResume() {
    if (!resumeTok.trim()) {
      setResumeMsg('Paste approval token first');
      return;
    }
    setResumeMsg('Submitting…');
    try {
      const r = await fetch(`/api/opo/research/tasks/${task.task_id}/resume-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approval_token: resumeTok.trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
      setResumeMsg('Resume queued. Poll task for progress.');
      if (onRefresh) onRefresh();
    } catch (e) {
      setResumeMsg(e.message);
    }
  }

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <Badge s={task.status} />
        {task.skill_fastpath_skill_id != null && Number(task.skill_fastpath_skill_id) > 0 && (
          <span style={{
            fontSize: 10,
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: 6,
            background: 'rgba(99,102,241,0.15)',
            color: 'var(--primary-color)',
            border: '1px solid var(--primary-color)',
          }}>cached plan</span>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
            {(task.query || '').slice(0, 100)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
            user:{task.user_id} · gfa:{task.gfa_id} · {task.task_mode || 'direct'}
            {elapsed != null ? ` · ${elapsed}s` : ''}
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {task.created_at ? new Date(task.created_at).toLocaleString() : ''}
        </div>
        <span style={{ color: 'var(--primary-color)', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--border-color)', paddingTop: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Task ID</div>
              <code style={{ fontSize: 11, color: 'var(--text-primary)' }}>{task.task_id}</code>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Phase</div>
              <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{task.phase || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Progress</div>
              <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{task.progress_pct ?? 0}%</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Elapsed</div>
              <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{elapsed != null ? `${elapsed}s` : '—'}</div>
            </div>
          </div>

          {task.pending_approval && task.pending_approval.approval_id != null && (
            <div style={{
              marginBottom: 10,
              padding: '8px',
              borderRadius: 'var(--border-radius)',
              border: '1px solid darkorange',
              background: 'rgba(255,140,0,0.06)',
              fontSize: 12,
            }}>
              <strong>Awaiting approval</strong> — approval gate id #{task.pending_approval.approval_id}
              {task.pending_approval.tool ? ` (${task.pending_approval.tool})` : ''}.
              Approve under the Approvals tab, paste token below, then Resume.
            </div>
          )}

          {(task.status === 'awaiting_approval' || task.phase === 'awaiting_approval') && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, marginBottom: 6, color: 'var(--text-secondary)' }}>
                Resume with one-shot UUID from Approvals approve response
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  value={resumeTok}
                  onChange={(e) => setResumeTok(e.target.value)}
                  placeholder="approval_token UUID"
                  style={{ ...inputStyle, flex: 1, minWidth: 240 }}
                  onClick={(e) => e.stopPropagation()}
                />
                <button type="button" style={btn('primary')} onClick={(e) => { e.stopPropagation(); sendResume(); }}>
                  Resume
                </button>
              </div>
              {resumeMsg && <div style={{ fontSize: 11, marginTop: 6, color: 'var(--text-secondary)' }}>{resumeMsg}</div>}
            </div>
          )}

          {task.error && (
            <div style={{
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid var(--danger-color)',
              borderRadius: 'var(--border-radius)',
              padding: '8px 10px',
              fontSize: 12,
              color: 'var(--danger-color)',
              marginBottom: 10,
            }}>{task.error}</div>
          )}

          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Subtask trace</div>
            <SubtaskTrace log={task.subtask_log} />
          </div>

          {task.final_response && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Final response (first 800 chars)</div>
              <div style={{
                background: 'var(--surface-color)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--border-radius)',
                padding: '8px 10px',
                fontSize: 12,
                color: 'var(--text-primary)',
                maxHeight: 200,
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
              }}>{task.final_response.slice(0, 800)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminCorneliusTasks() {
  const [tasks,   setTasks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId,  setUserId]  = useState('13');
  const [filter,  setFilter]  = useState('all');
  const [error,   setError]   = useState('');

  const fetchTasks = useCallback(async () => {
    if (!userId.trim()) { setTasks([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`/api/opo/research/tasks?user_id=${userId.trim()}&limit=50`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTasks(data.tasks || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const filtered = tasks.filter(t => filter === 'all' || t.status === filter);

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
        Admin view of all research tasks with subtask traces, timings, and sources.
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          value={userId}
          onChange={e => setUserId(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchTasks()}
          placeholder="User ID (press Enter)"
          style={{ ...inputStyle, width: 200 }}
        />
        <select value={filter} onChange={e => setFilter(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="all">All statuses</option>
          <option value="queued">Queued</option>
          <option value="running">Running</option>
          <option value="awaiting_approval">Awaiting approval</option>
          <option value="complete">Complete</option>
          <option value="failed">Failed</option>
          <option value="abandoned">Abandoned</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button onClick={fetchTasks} style={btn('primary')}>Refresh</button>
      </div>

      {error && <div style={{ color: 'var(--danger-color)', fontSize: 13, marginBottom: 12 }}>Error: {error}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          {userId ? 'No tasks found for this user.' : 'Enter a user ID above to load tasks.'}
        </div>
      )}

      {filtered.map(task => <TaskRow key={task.task_id} task={task} onRefresh={fetchTasks} />)}
    </div>
  );
}
